/**
 * TypeScript DTOs for database-driven regulation system
 * These replace the hardcoded DataRequestTypeDto and related enums
 * These enums must match the Kotlin enums exactly
 */

/**
 * Enum representing workflow types for privacy regulation requests
 * Maps to PostgreSQL workflow_type_enum
 */
export enum WorkflowType {
  KNOW = "KNOW",
  DELETE = "DELETE",
  OPT_OUT = "OPT_OUT",
  PORTABILITY = "PORTABILITY",
  CORRECTION = "CORRECTION",
  OBJECTION = "OBJECTION",
  RESTRICT_PROCESSING = "RESTRICT_PROCESSING",
  PIPEDA_WITHDRAW_CONSENT = "PIPEDA_WITHDRAW_CONSENT",
  WITHDRAW_CONSENT = "WITHDRAW_CONSENT",
  LIMIT = "LIMIT",
  PROFILING_OPT_OUT = "PROFILING_OPT_OUT",
  APPEAL = "APPEAL",
  PROFILING_INFORMATION = "PROFILING_INFORMATION",
  GPC_OPT_OUT = "GPC_OPT_OUT",
  BROWSER_OPT_OUT = "BROWSER_OPT_OUT",
  US_WITHDRAW_CONSENT = "US_WITHDRAW_CONSENT",
}

/**
 * Enum representing form types for privacy regulation requests
 * Maps to PostgreSQL form_type_enum
 */
export enum FormType {
  YPC = "YPC", // Your Privacy Choices
  DSAR = "DSAR", // Data Subject Access Request
}

/**
 * Enum representing region groups for privacy regulations
 * Maps to PostgreSQL region_group_enum
 */
export enum RegionGroup {
  UNITED_STATES = "UNITED_STATES",
  EEA_UK = "EEA_UK",
  CANADA = "CANADA",
  OTHER = "OTHER",
}

/**
 * Enum representing regulation slugs
 * Maps to PostgreSQL regulation_slug_enum
 */
export enum RegulationSlug {
  GDPR = "GDPR",
  PIPEDA = "PIPEDA",
  CCPA = "CCPA",
  VOLUNTARY = "VOLUNTARY",
  VCDPA = "VCDPA",
  CPA = "CPA",
  CTDPA = "CTDPA",
  TDPSA = "TDPSA",
  MCDPA = "MCDPA",
  OCPA = "OCPA",
  UTCPA = "UTCPA",
  ICDPA = "ICDPA",
  DPDPA = "DPDPA",
  NHPA = "NHPA",
  NDPA = "NDPA",
  NJDPA = "NJDPA",
  NPICICA = "NPICICA",
  MODPA = "MODPA",
  INCDPA = "INCDPA",
  KYCDPA = "KYCDPA",
  RIDTPPA = "RIDTPPA",
  TIPA = "TIPA",
  MNCDPA = "MNCDPA",
}

export interface RegulationRequestTypeDto {
  id: string;

  // Request type information
  label: string;
  shortLabel: string;
  workflowType: WorkflowType;
  formType: FormType;

  // Slug-based identification for efficient operations
  requestTypeSlug: string; // Computed slug: "${regulationSlug}_${workflowType}"

  // Configuration
  isDueFromVerificationDate: boolean;
  initialDueDateDays: number;
  extendDueDateDays: number;
  maxDaysOpen: number;
  sendClosedNotVerifiedNotification: boolean;
  requireVerification: boolean;

  // Regulation information
  regulationLaw: RegulationLawDto;

  // Convenience properties derived from workflow
  isDelete: boolean;
  isKnow: boolean;
  isGdpr: boolean;
}

export interface RegulationLawDto {
  id: string;
  abbreviation: string;
  name: string; // Required in backend
  slug: RegulationSlug; // Required enum in backend
  effectiveDate?: string; // ISO date string (LocalDate in backend)
  noticeAnchor?: string;
  region?: RegulationRegionDto; // Include region for display
}

export interface RegulationRegionDto {
  id: string;
  regionCode: string;
  regionName: string;
  regionGroup: RegionGroup;
}

/**
 * Simplified DTO for region request type mapping used in privacy center
 * Provides a flattened view of request types grouped by region
 */
export interface SimplifiedRegionRequestTypeDto {
  anchor?: string;
  optOutRequestTypes: string[];
  dsarRequestTypes: string[];
  regionName: string;
  regulation: string;
  requestTypeLangKeyMap?: Record<string, string>; // Map request types to their i18n keys
  regionNameLangKey?: string; // i18n key for region name
  regionGroupLangKey?: string; // i18n key for region group
  regionGroup?: string; // e.g., "UNITED_STATES", "EEA_UK", "CANADA"
}
